import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ReliabotixLogo from '../components/ReliabotixLogo';

const { FiMail, FiLinkedin, FiGithub, FiCalendar, FiSend, FiMapPin, FiClock, FiMessageCircle, FiCheckCircle, FiAlertCircle, FiX } = FiIcons;

const Contact = () => {
  const [headerRef, headerInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [formRef, formInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [showCalendar, setShowCalendar] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    useCase: '',
    timeline: '',
    budgetRange: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Simulate form submission delay
    setTimeout(() => {
      setSubmitStatus('success');
      setIsSubmitting(false);
      // Reset form after successful submission
      setFormData({
        name: '',
        email: '',
        company: '',
        useCase: '',
        timeline: '',
        budgetRange: '',
        message: ''
      });
    }, 1500);
  };

  const handleBookCall = (e) => {
    e.preventDefault();
    setShowCalendar(true);
  };

  const closeCalendar = () => {
    setShowCalendar(false);
  };

  const contactMethods = [
    {
      icon: FiMail,
      title: 'Email',
      value: 'info@reliabotix.com',
      link: 'mailto:info@reliabotix.com',
      description: 'Get a response within 4 hours',
      gradient: 'from-red-500 to-pink-500'
    },
    {
      icon: FiLinkedin,
      title: 'LinkedIn',
      value: 'Reliabotix Solutions',
      link: 'https://linkedin.com/in/guychenya',
      description: 'Connect professionally',
      gradient: 'from-blue-500 to-indigo-500'
    },
    {
      icon: FiCalendar,
      title: 'Schedule Call',
      value: 'Book 30-min consultation',
      action: handleBookCall,
      description: 'Free strategy session',
      gradient: 'from-purple-500 to-pink-500'
    }
  ];

  return (
    <div className="relative overflow-hidden">
      {/* Calendar Modal */}
      <AnimatePresence>
        {showCalendar && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
            onClick={closeCalendar}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 50 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-4xl mx-4 max-h-[90vh] bg-dark-900/95 backdrop-blur-xl rounded-3xl border border-dark-700/50 overflow-hidden shadow-2xl"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-dark-700/30">
                <div className="flex items-center">
                  <ReliabotixLogo size={40} variant="minimal" animate={false} />
                  <div className="ml-3">
                    <h3 className="text-xl font-bold text-white">Schedule Discovery Call</h3>
                    <p className="text-gray-400 text-sm">Free 30-minute consultation</p>
                  </div>
                </div>
                <button
                  onClick={closeCalendar}
                  className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-dark-700/50"
                  title="Close (ESC)"
                >
                  <SafeIcon icon={FiX} className="w-6 h-6" />
                </button>
              </div>

              {/* Calendar Content */}
              <div className="h-[600px] bg-gradient-to-br from-dark-800 to-dark-900">
                <iframe
                  src="https://cal.com/guychenya?embed=true"
                  className="w-full h-full border-0"
                  allow="camera;microphone;geolocation"
                  title="Schedule a Call with Reliabotix"
                />
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-dark-700/30 bg-dark-800/50">
                <p className="text-center text-gray-400 text-sm">
                  Press <kbd className="px-2 py-1 bg-dark-600/50 text-gray-300 rounded text-xs">ESC</kbd> to close
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Background Elements */}
      <div className="floating-orb floating-orb-1"></div>
      <div className="floating-orb floating-orb-2"></div>

      {/* Header */}
      <section ref={headerRef} className="py-24 lg:py-32 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={headerInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-6xl sm:text-7xl lg:text-8xl font-bold mb-8">
              <span className="bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-transparent">
                Let's Build the Future
              </span>
            </h1>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Ready to transform your business with intelligent AI solutions? Our experts are here to discuss your vision and create a customized roadmap for success.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-16 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {contactMethods.map((method, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                whileHover={{ y: -8, scale: 1.02 }}
                className="group"
              >
                {method.action ? (
                  <button
                    onClick={method.action}
                    className="w-full glass-card p-6 rounded-2xl transition-all duration-500 text-center"
                  >
                    <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r ${method.gradient} rounded-2xl mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <SafeIcon icon={method.icon} className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-primary-300 transition-colors">
                      {method.title}
                    </h3>
                    <p className="text-primary-400 font-medium mb-2">{method.value}</p>
                    <p className="text-gray-400 text-sm">{method.description}</p>
                  </button>
                ) : (
                  <a
                    href={method.link}
                    target={method.link.startsWith('http') ? '_blank' : undefined}
                    rel={method.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="block glass-card p-6 rounded-2xl transition-all duration-500 text-center"
                  >
                    <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r ${method.gradient} rounded-2xl mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <SafeIcon icon={method.icon} className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-primary-300 transition-colors">
                      {method.title}
                    </h3>
                    <p className="text-primary-400 font-medium mb-2">{method.value}</p>
                    <p className="text-gray-400 text-sm">{method.description}</p>
                  </a>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Contact Form */}
      <section ref={formRef} className="py-20 relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={formInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="glass-strong rounded-3xl p-8 lg:p-12"
          >
            <div className="text-center mb-8">
              <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">Start Your AI Journey</h2>
              <p className="text-gray-300 text-lg">
                Tell us about your vision and we'll create a customized proposal within 24 hours
              </p>
            </div>

            {/* Submit Status Messages */}
            {submitStatus === 'success' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-6 bg-green-500/20 border border-green-500/30 rounded-xl text-green-300 text-center"
              >
                <div className="flex items-center justify-center mb-3">
                  <SafeIcon icon={FiCheckCircle} className="w-8 h-8 mr-3" />
                  <h3 className="text-xl font-semibold">Thank You!</h3>
                </div>
                <p className="text-lg mb-2">Your message has been received successfully.</p>
                <p className="text-sm text-green-200">
                  We'll get back to you within 4 hours during business hours. For urgent matters, please contact us directly at info@reliabotix.com
                </p>
              </motion.div>
            )}

            {!submitStatus && (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:bg-white/15 transition-all duration-200"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                      Business Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:bg-white/15 transition-all duration-200"
                      placeholder="your@company.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-2">
                      Company Name
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:bg-white/15 transition-all duration-200"
                      placeholder="Your company"
                    />
                  </div>
                  <div>
                    <label htmlFor="timeline" className="block text-sm font-medium text-gray-300 mb-2">
                      Project Timeline
                    </label>
                    <select
                      id="timeline"
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:bg-white/15 transition-all duration-200"
                    >
                      <option value="" className="bg-dark-800 text-white">Select timeline</option>
                      <option value="immediate" className="bg-dark-800 text-white">Immediate (1-2 weeks)</option>
                      <option value="short" className="bg-dark-800 text-white">Short-term (1-2 months)</option>
                      <option value="medium" className="bg-dark-800 text-white">Medium-term (3-6 months)</option>
                      <option value="long" className="bg-dark-800 text-white">Long-term (6+ months)</option>
                      <option value="exploring" className="bg-dark-800 text-white">Just exploring</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="useCase" className="block text-sm font-medium text-gray-300 mb-2">
                      Primary Use Case
                    </label>
                    <select
                      id="useCase"
                      name="useCase"
                      value={formData.useCase}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:bg-white/15 transition-all duration-200"
                    >
                      <option value="" className="bg-dark-800 text-white">Select use case</option>
                      <option value="automation" className="bg-dark-800 text-white">Process Automation</option>
                      <option value="analytics" className="bg-dark-800 text-white">Predictive Analytics</option>
                      <option value="chatbot" className="bg-dark-800 text-white">Conversational AI</option>
                      <option value="integration" className="bg-dark-800 text-white">System Integration</option>
                      <option value="custom" className="bg-dark-800 text-white">Custom AI Solution</option>
                      <option value="consulting" className="bg-dark-800 text-white">Strategic Consulting</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="budgetRange" className="block text-sm font-medium text-gray-300 mb-2">
                      Investment Range
                    </label>
                    <select
                      id="budgetRange"
                      name="budgetRange"
                      value={formData.budgetRange}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:bg-white/15 transition-all duration-200"
                    >
                      <option value="" className="bg-dark-800 text-white">Select range</option>
                      <option value="startup" className="bg-dark-800 text-white">$10K - $25K</option>
                      <option value="growth" className="bg-dark-800 text-white">$25K - $75K</option>
                      <option value="enterprise" className="bg-dark-800 text-white">$75K - $200K</option>
                      <option value="strategic" className="bg-dark-800 text-white">$200K+</option>
                      <option value="discuss" className="bg-dark-800 text-white">Let's discuss</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                    Project Details
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows="5"
                    value={formData.message}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:bg-white/15 transition-all duration-200 resize-none"
                    placeholder="Describe your vision, current challenges, and what success looks like for your organization..."
                  ></textarea>
                </div>

                <div className="text-center">
                  <motion.button
                    type="submit"
                    disabled={isSubmitting || !formData.name || !formData.email}
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    className="inline-flex items-center px-10 py-5 bg-gradient-to-r from-primary-500 to-accent-purple text-white rounded-2xl font-semibold hover:shadow-2xl hover:shadow-primary-500/25 transition-all duration-300 glass-button disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <SafeIcon icon={FiSend} className="w-6 h-6 mr-3" />
                        Send Message
                      </>
                    )}
                  </motion.button>
                </div>

                <div className="text-center text-sm text-gray-400 mt-4">
                  <p>Your message will be sent directly to our team at info@reliabotix.com</p>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </section>

      {/* Response Guarantee */}
      <section className="py-16 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="glass-strong rounded-3xl p-8 text-center">
            <div className="flex items-center justify-center mb-4">
              <SafeIcon icon={FiClock} className="w-8 h-8 text-primary-400 mr-3" />
              <h3 className="text-xl font-semibold text-primary-300">Response Guarantee</h3>
            </div>
            <p className="text-primary-200 mb-4 text-lg">
              We respond to all business inquiries within 4 hours during business hours.
            </p>
            <p className="text-primary-300 text-sm">
              For urgent matters, email us directly at info@reliabotix.com or connect via LinkedIn for immediate assistance.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;